import { plainToInstance } from "class-transformer"
import * as PapaParse from "papaparse"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import type { IOneVerifyCreateNetworkStatusRuleRequestData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { INetworkStatusRule } from "@common-shared/types/responseData/commonResponseData.types"

import { NETWORK_STATUS_CONFIG } from "../config/networkStatus.config"
import { RawNetworkStatusRuleCsvRowDTO } from "../dto/rawNetworkStatusRuleCsvInput.dto"
import { NetworkStatusExportRowKey } from "../types/networkStatus.types.ts"

export class NetworkStatusRulesCsvHelper {
    /**
     * Parse CSV text (first row headers, then data). No version line.
     * Excludes empty lines and any line that starts with # (comment lines).
     * Returns rules with index and any parse errors per row.
     */
    static parse(
        csvText: string,
    ): IOneVerifyCreateNetworkStatusRuleRequestData[] {
        const lines = csvText.split(/\r?\n/)

        const fromHeader = lines.slice(NETWORK_STATUS_CONFIG.csvHeaderRowIndex)

        const headerLine = fromHeader[0] ?? ""

        // Exclude empty lines, comment lines (#), and lines that are all-empty cells.
        const dataLines = fromHeader.slice(1).filter(line => {
            const trimmed = line.trim()

            if (trimmed.length === 0 || trimmed.startsWith("#")) {
                return false
            }

            if (/^[\s,]*$/.test(trimmed)) {
                return false
            }

            const withoutQuotedEmpties = trimmed.replace(/"\s*"/g, "")

            if (/^[\s,]*$/.test(withoutQuotedEmpties)) {
                return false
            }

            return true
        })

        const csvWithHeadersAndData = [headerLine, ...dataLines].join("\n")

        const parsed = PapaParse.parse<Record<string, string>>(
            csvWithHeadersAndData,
            {
                header: true,
                skipEmptyLines: true,
            },
        )

        const rules: RawNetworkStatusRuleCsvRowDTO[] = []

        const importDate = new Date().toISOString().slice(0, 10)

        const arrayKeys = new Set<NetworkStatusExportRowKey>(
            NETWORK_STATUS_CONFIG.csvArrayRowKeys,
        )

        parsed.data.forEach(row => {
            const raw: Record<string, string | string[]> = {}

            for (const [header, value] of Object.entries(row)) {
                const trimmedHeader = header.trim()

                if (
                    Object.keys(
                        NETWORK_STATUS_CONFIG.csvHeaderToKeyIgnored,
                    ).includes(trimmedHeader)
                ) {
                    continue
                }

                const key = NETWORK_STATUS_CONFIG.csvHeaderToKey[
                    trimmedHeader
                ] as NetworkStatusExportRowKey | undefined

                if (key === undefined || value === undefined) {
                    continue
                }

                const str = String(value).trim()

                if (arrayKeys.has(key)) {
                    raw[key] = str
                        .split(",")
                        .map(s => s.trim())
                        .filter(Boolean)
                } else {
                    raw[key] = str
                }
            }

            if (this.isEmptyRow(raw)) {
                return
            }

            const dto = plainToInstance(RawNetworkStatusRuleCsvRowDTO, raw)

            dto.index = rules.length

            dto.name =
                dto.name || `Import ${importDate} - Rule #${rules.length + 1}`

            rules.push(dto)
        })

        return rules as IOneVerifyCreateNetworkStatusRuleRequestData[]
    }

    /**
     * Map a single rule to a CSV row in upload-compatible column order.
     * Same columns and order as upload template so downloaded CSV can be used for bulk create.
     * Network Status column uses enum values "IN" / "OUT" (accepted by upload parser and API).
     */
    static ruleToCsvRow(rule: INetworkStatusRule): string[] {
        const networkStatusValue =
            rule.networkStatus === PayerCoverageCheckNetwork.IN
                ? PayerCoverageCheckNetwork.IN
                : rule.networkStatus === PayerCoverageCheckNetwork.OUT
                  ? PayerCoverageCheckNetwork.OUT
                  : String(rule.networkStatus ?? "")

        const valueByKey: Record<NetworkStatusExportRowKey, string> = {
            networkStatus: networkStatusValue,
            payerId: rule.payer?.payerId ?? "",
            providerNpi: rule.providerNpi,
            startDate: rule.startDate ?? "",
            endDate: rule.endDate ?? "",
            thirdPartyAdministratorId:
                rule.thirdPartyAdministrator?.payerId ?? "",
            insuranceTypes: rule.insuranceTypes?.join(", ") ?? "",
            planTypes: rule.planTypes?.join(", ") ?? "",
            planName: rule.planName ?? "",
            groupName: rule.groupName ?? "",
            state: rule.state ?? "",
            name: rule.name ?? "",
            id: rule.id ? String(rule.id) : "",
        }

        return Object.keys(NETWORK_STATUS_CONFIG.csvExportHeadersToKey).map(
            header => {
                const valueKey: NetworkStatusExportRowKey | undefined =
                    NETWORK_STATUS_CONFIG.csvExportHeadersToKey[header]

                return valueKey ? (valueByKey[valueKey] ?? "") : ""
            },
        )
    }

    /**
     * Check if a row is empty (all values are empty or whitespace-only). Used to exclude empty lines that may be parsed as rows.
     */
    private static isEmptyRow(row: Record<string, unknown>): boolean {
        return Object.values(row).every(v => /^[\s,]*$/.test(String(v ?? "")))
    }
}
